import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle, Heart, User, Mail, Lock, Phone, Eye, EyeOff } from '../icons';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { acceptInvite, register, login, getInviteInfo, getMe, switchRole, forgotPassword } from '../services/api';
import { isNative, openGoogleSignIn } from '../utils/native';

function AcceptInvite() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, loginUser, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, login-required, success, error, invalid
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [authMode, setAuthMode] = useState('register'); // register, login
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', password: '', confirmPassword: '' });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [infoLoaded, setInfoLoaded] = useState(false);
  const [googleAccount, setGoogleAccount] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [switchFailed, setSwitchFailed] = useState(false);
  // User id the invite was already accepted for, so a session refresh
  // (role switch, Google, register) doesn't accept it a second time.
  const acceptedFor = useRef(null);

  // Fetch invite info on mount (public endpoint, no auth needed)
  useEffect(() => {
    getInviteInfo(token)
      .then((res) => {
        setInviteInfo(res.data);
        // Pre-fill email from invitation
        if (res.data.invited_email) {
          setFormData(prev => ({ ...prev, email: res.data.invited_email }));
        }
        // An existing account, or a link that was already used: log in rather
        // than create a second account
        if (res.data.userExists || res.data.invite_status === 'accepted') {
          setAuthMode('login');
        }
      })
      .catch((err) => {
        // Removed or mistyped link: say so now, not after they fill in a form.
        // Anything else is non-critical — just won't show contextual message.
        if (err.response?.status === 404) setStatus('invalid');
      })
      .finally(() => setInfoLoaded(true));
  }, [token]);

  // Google sign-in without a popup comes back here with ?token= or ?error=
  useEffect(() => {
    const urlToken = searchParams.get('token');
    const urlError = searchParams.get('error');
    if (!urlToken && !urlError) return;
    window.history.replaceState({}, '', window.location.pathname);
    if (urlError) {
      setFormError(urlError);
      return;
    }
    localStorage.setItem('nepwed_token', urlToken);
    getMe()
      .then((res) => loginUser(res.data, urlToken))
      .catch(() => {
        localStorage.removeItem('nepwed_token');
        setFormError('Google login failed. Please try again.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Google popup result. A session opened here is handled like a password
  // login: the accept effect below takes over.
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'social-auth-error' && event.data.error) {
        setFormError(event.data.error);
      } else if (event.data?.type === 'social-auth' && event.data.token && event.data.user) {
        loginUser(event.data.user, event.data.token);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Signed in as someone other than the invitee — typically the couple opening
  // the link they just sent. Accepting would attach the invite to the wrong
  // account, so ask them to sign out instead of accepting automatically.
  const signedInAsOther = !!(user && inviteInfo?.invited_email
    && (user.email || '').toLowerCase() !== inviteInfo.invited_email.toLowerCase());

  // Accepting doesn't move the session: a couple stays on their own dashboard
  // (whose checklist is their own wedding), and register / Google responses
  // carry a trimmed profile. A helper-scoped token fixes both.
  const openHelperView = async () => {
    try {
      const sw = await switchRole('helper');
      loginUser(sw.data.user, sw.data.token);
    } catch {
      setSwitchFailed(true);
    }
  };

  useEffect(() => {
    if (authLoading || !infoLoaded || status === 'invalid') return;
    if (!user) {
      setStatus('login-required');
      return;
    }
    if (signedInAsOther || acceptedFor.current === user.id) return;
    acceptedFor.current = user.id;
    setStatus('loading');

    acceptInvite(token)
      .then(async (res) => {
        setResult(res.data);
        await openHelperView();
        setStatus('success');
      })
      .catch((err) => {
        setError(err.response?.data?.error || 'Failed to accept invitation');
        setStatus('error');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user, authLoading, infoLoaded, signedInAsOther]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setFormError('');
    const { firstName, lastName, email, phone, password, confirmPassword } = formData;
    if (!firstName || !lastName || !email || !password) {
      setFormError(t('acceptInvite.fillRequired'));
      return;
    }
    if (password.length < 6) {
      setFormError(t('acceptInvite.passwordMinLength'));
      return;
    }
    if (password !== confirmPassword) {
      setFormError(t('acceptInvite.passwordsNoMatch'));
      return;
    }
    setFormLoading(true);
    try {
      const res = await register({ firstName, lastName, email, phone, password, role: 'helper', inviteToken: token });
      // Registering with the invite token already accepts it; accepting again
      // would only report "already accepted".
      acceptedFor.current = res.data.user.id;
      setResult({ role: inviteInfo?.role || 'helper', wedding: inviteInfo ? { partner1_name: inviteInfo.partner1_name, partner2_name: inviteInfo.partner2_name } : null });
      loginUser(res.data.user, res.data.token);
      await openHelperView();
      setStatus('success');
    } catch (err) {
      const data = err.response?.data;
      if (data?.isHelperAccount || data?.error === 'Email already exists') {
        setAuthMode('login');
        setFormError(t('acceptInvite.emailExists'));
      } else {
        setFormError(data?.error || t('acceptInvite.registerFailed'));
      }
      setFormLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setFormError('');
    setGoogleAccount(false);
    const { email, password } = formData;
    if (!email || !password) {
      setFormError(t('acceptInvite.enterEmailPassword'));
      return;
    }
    setFormLoading(true);
    try {
      const res = await login({ email, password });
      loginUser(res.data.user, res.data.token);
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === 'GOOGLE_ACCOUNT') {
        // The server text says "log in with Google"; the app has no Google button
        setGoogleAccount(true);
        setFormError(isNative() ? t('login.googleAccountNoGoogle') : t('login.googleAccountWeb'));
      } else {
        setFormError(data?.error || t('acceptInvite.loginFailed'));
      }
      setFormLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!formData.email) return;
    setForgotLoading(true);
    setFormError('');
    try {
      await forgotPassword(formData.email);
      setForgotSent(true);
    } catch (err) {
      // The server answers the same for known and unknown emails, so an error
      // here is a rate limit or a network failure and reveals nothing.
      setFormError(err.response?.data?.error || t('resetPassword.genericError'));
    }
    setForgotLoading(false);
  };

  const openForgot = () => {
    setShowForgot(true);
    setForgotSent(false);
    setFormError('');
  };

  if (status === 'invalid') {
    return (
      <div className="invite-page">
        <div className="container">
          <div className="invite-card error">
            <h2>{t('acceptInvite.oops')}</h2>
            <p>{t('acceptInvite.invalidLink')}</p>
            <button className="btn btn-primary" onClick={() => navigate(user ? '/dashboard' : '/')}>
              {user ? t('acceptInvite.goToDashboard') : t('acceptInvite.goHome')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="invite-page">
        <div className="container">
          <div className="invite-card error">
            <h2>{t('acceptInvite.oops')}</h2>
            <p>{error}</p>
            <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
              {t('acceptInvite.goToDashboard')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    const names = result?.wedding?.partner1_name ? `${result.wedding.partner1_name} & ${result.wedding.partner2_name}` : '';
    const isHelper = result?.role !== 'delegate';
    return (
      <div className="invite-page">
        <div className="container">
          <div className="invite-card success">
            <CheckCircle size={48} className="text-green" />
            <h2>{t('acceptInvite.invitationAccepted')} 🎉</h2>
            {names && (
              <p>
                {t('acceptInvite.nowRole')
                  .replace('{role}', isHelper ? t('acceptInvite.roleHelper') : t('acceptInvite.roleDelegate'))
                  .replace('{names}', names)}
              </p>
            )}
            <p className="invite-desc">
              {isHelper ? t('acceptInvite.helperDesc') : t('acceptInvite.delegateDesc')}
            </p>
            {switchFailed && <p className="invite-desc">{t('acceptInvite.switchHint')}</p>}
            <div className="invite-actions">
              <button className="btn btn-primary" onClick={() => navigate(isHelper ? '/checklist' : '/dashboard')}>
                {isHelper ? t('acceptInvite.viewChecklist') : t('acceptInvite.goToDashboard')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (infoLoaded && signedInAsOther) {
    return (
      <div className="invite-page">
        <div className="container">
          <div className="invite-card" style={{ maxWidth: '480px', textAlign: 'center' }}>
            <Heart size={40} className="heart-icon" style={{ color: '#DC143C', marginBottom: '12px' }} />
            <h2 style={{ margin: '0 0 8px' }}>{t('acceptInvite.otherAccountTitle')}</h2>
            <p style={{ color: '#6b7280', marginBottom: '24px', fontSize: '14px' }}>
              {t('acceptInvite.otherAccountText')
                .replace('{names}', `${inviteInfo.partner1_name} & ${inviteInfo.partner2_name}`)
                .replace('{invited}', inviteInfo.invited_email)
                .replace('{current}', user.email)}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button className="btn btn-primary" onClick={logout}
                style={{ padding: '12px 24px', fontSize: '15px', display: 'flex', justifyContent: 'center' }}>
                {t('acceptInvite.signOutContinue')}
              </button>
              <button className="btn btn-outline" onClick={() => navigate('/')}
                style={{ padding: '10px 24px', fontSize: '14px', display: 'flex', justifyContent: 'center' }}>
                {t('acceptInvite.notNow')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div className="invite-page">
        <div className="container">
          <div className="invite-card">
            <div className="spinner"></div>
            <p>{t('acceptInvite.accepting')}</p>
          </div>
        </div>
      </div>
    );
  }

  // status === 'login-required'
  const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px', color: '#374151' };
  const linkButtonStyle = { background: 'none', border: 'none', color: '#be185d', fontSize: '13px', fontWeight: 500, cursor: 'pointer', padding: '4px 8px' };
  const alreadyUsed = inviteInfo?.invite_status === 'accepted';

  // Google sign-in is browser-only (see Login.js); in the app the
  // Google-account message points to "set a password" instead.
  const googleOption = !isNative() && (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '16px 0' }}>
        <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
        <span style={{ fontSize: '13px', color: '#9ca3af' }}>{t('login.orContinueWith')}</span>
        <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
      </div>
      <button
        type="button"
        className="btn btn-block"
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: '#fff', border: '1px solid #d1d5db', color: '#374151', fontWeight: 500, padding: '10px 16px', borderRadius: '8px', cursor: 'pointer' }}
        disabled={formLoading}
        onClick={() => { setFormError(''); setGoogleAccount(false); openGoogleSignIn(`/invite/accept/${token}`); }}
      >
        <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
        Google
      </button>
    </>
  );

  return (
    <div className="invite-page">
      <div className="container">
        <div className="invite-card" style={{ maxWidth: '480px', textAlign: 'left' }}>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <Heart size={40} className="heart-icon" style={{ color: '#DC143C' }} />
            <h2 style={{ margin: '12px 0 4px' }}>{t('acceptInvite.weddingInvitation')}</h2>
            {inviteInfo && inviteInfo.partner1_name ? (
              <p style={{ color: '#6b7280', fontSize: '14px' }}>
                {authMode === 'register'
                  ? `${t('acceptInvite.createAccountToHelp')} ${inviteInfo.partner1_name} & ${inviteInfo.partner2_name}${t('acceptInvite.wedding')}`
                  : `${t('acceptInvite.loginToHelp')} ${inviteInfo.partner1_name} & ${inviteInfo.partner2_name}${t('acceptInvite.wedding')}`}
              </p>
            ) : (
              <p style={{ color: '#6b7280', fontSize: '14px' }}>
                {authMode === 'register' ? t('acceptInvite.createAccountGeneric') : t('acceptInvite.loginGeneric')}
              </p>
            )}
          </div>

          {alreadyUsed && (
            <div style={{ background: '#fffbeb', color: '#92400e', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>
              {t('acceptInvite.alreadyUsed')}
            </div>
          )}

          {/* Tabs. A used link can only be reopened by the account that accepted it. */}
          <div style={{ display: 'flex', gap: '0', marginBottom: '20px', borderBottom: '2px solid #e5e7eb' }}>
            {!alreadyUsed && (
              <button onClick={() => { setAuthMode('register'); setFormError(''); setGoogleAccount(false); setShowForgot(false); }}
                style={{ flex: 1, padding: '10px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 600,
                  color: authMode === 'register' ? '#DC143C' : '#9ca3af',
                  borderBottom: authMode === 'register' ? '2px solid #DC143C' : '2px solid transparent', marginBottom: '-2px' }}>
                {t('acceptInvite.createAccount')}
              </button>
            )}
            <button onClick={() => { setAuthMode('login'); setFormError(''); }}
              style={{ flex: 1, padding: '10px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 600,
                color: authMode === 'login' ? '#DC143C' : '#9ca3af',
                borderBottom: authMode === 'login' ? '2px solid #DC143C' : '2px solid transparent', marginBottom: '-2px' }}>
              {t('acceptInvite.logIn')}
            </button>
          </div>

          {formError && (
            <div style={{ background: '#fef2f2', color: '#dc2626', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>
              {formError}
              {googleAccount && !showForgot && (
                <div style={{ marginTop: '8px' }}>
                  <button type="button" onClick={openForgot} style={{ ...linkButtonStyle, padding: 0, textDecoration: 'underline' }}>
                    {t('login.setPassword')}
                  </button>
                </div>
              )}
            </div>
          )}

          {authMode === 'register' ? (
            <>
              <form onSubmit={handleRegister}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={labelStyle}>{t('acceptInvite.firstName')}</label>
                    <input style={inputStyle} placeholder={t('register.firstName')} value={formData.firstName}
                      onChange={e => setFormData(p => ({ ...p, firstName: e.target.value }))} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t('acceptInvite.lastName')}</label>
                    <input style={inputStyle} placeholder={t('register.lastName')} value={formData.lastName}
                      onChange={e => setFormData(p => ({ ...p, lastName: e.target.value }))} />
                  </div>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={labelStyle}>{t('acceptInvite.emailLabel')}</label>
                  <div style={{ position: 'relative' }}>
                    <Mail size={16} style={{ position: 'absolute', left: '10px', top: '12px', color: '#9ca3af' }} />
                    <input style={{ ...inputStyle, paddingLeft: '34px' }} type="email" placeholder="you@example.com" value={formData.email}
                      onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} />
                  </div>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={labelStyle}>{t('acceptInvite.phone')}</label>
                  <div style={{ position: 'relative' }}>
                    <Phone size={16} style={{ position: 'absolute', left: '10px', top: '12px', color: '#9ca3af' }} />
                    <input style={{ ...inputStyle, paddingLeft: '34px' }} type="tel" placeholder={t('acceptInvite.phonePlaceholder')} value={formData.phone}
                      onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} />
                  </div>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={labelStyle}>{t('acceptInvite.password')}</label>
                  <div style={{ position: 'relative' }}>
                    <Lock size={16} style={{ position: 'absolute', left: '10px', top: '12px', color: '#9ca3af' }} />
                    <input style={{ ...inputStyle, paddingLeft: '34px', paddingRight: '38px' }} type={showPassword ? 'text' : 'password'} placeholder={t('acceptInvite.passwordPlaceholder')} value={formData.password}
                      onChange={e => setFormData(p => ({ ...p, password: e.target.value }))} />
                    <button type="button" onClick={() => setShowPassword(v => !v)}
                      style={{ position: 'absolute', right: '8px', top: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '2px' }}>
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>{t('acceptInvite.confirmPassword')}</label>
                  <div style={{ position: 'relative' }}>
                    <Lock size={16} style={{ position: 'absolute', left: '10px', top: '12px', color: '#9ca3af' }} />
                    <input style={{ ...inputStyle, paddingLeft: '34px' }} type={showPassword ? 'text' : 'password'} placeholder={t('acceptInvite.confirmPlaceholder')} value={formData.confirmPassword}
                      onChange={e => setFormData(p => ({ ...p, confirmPassword: e.target.value }))} />
                  </div>
                </div>
                <button type="submit" className="btn btn-primary" disabled={formLoading}
                  style={{ width: '100%', padding: '12px', fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <User size={16} /> {formLoading ? t('acceptInvite.creatingAccount') : t('acceptInvite.createAndAccept')}
                </button>
              </form>
              {googleOption}
            </>
          ) : showForgot ? (
            <div>
              <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 12px' }}>{t('login.resetPasswordDesc')}</p>
              {forgotSent ? (
                <div style={{ background: '#DCFCE7', color: '#16A34A', padding: '12px 14px', borderRadius: '8px', fontSize: '13px' }}>
                  {t('login.resetSent')} {t('acceptInvite.resetComeBack')}
                </div>
              ) : (
                <form onSubmit={handleForgot}>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={labelStyle}>{t('login.emailAddress')}</label>
                    <div style={{ position: 'relative' }}>
                      <Mail size={16} style={{ position: 'absolute', left: '10px', top: '12px', color: '#9ca3af' }} />
                      <input style={{ ...inputStyle, paddingLeft: '34px' }} type="email" required placeholder="you@example.com" value={formData.email}
                        onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} />
                    </div>
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={forgotLoading}
                    style={{ width: '100%', padding: '12px', fontSize: '15px' }}>
                    {forgotLoading ? t('login.sending') : t('login.sendResetLink')}
                  </button>
                </form>
              )}
              <div style={{ textAlign: 'center', marginTop: '12px' }}>
                <button type="button" onClick={() => setShowForgot(false)} style={{ ...linkButtonStyle, color: '#6b7280' }}>
                  {t('login.backToLogin')}
                </button>
              </div>
            </div>
          ) : (
            <>
              <form onSubmit={handleLogin}>
                <div style={{ marginBottom: '12px' }}>
                  <label style={labelStyle}>{t('login.email')}</label>
                  <div style={{ position: 'relative' }}>
                    <Mail size={16} style={{ position: 'absolute', left: '10px', top: '12px', color: '#9ca3af' }} />
                    <input style={{ ...inputStyle, paddingLeft: '34px' }} type="email" placeholder="you@example.com" value={formData.email}
                      onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} />
                  </div>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <label style={labelStyle}>{t('login.password')}</label>
                  <div style={{ position: 'relative' }}>
                    <Lock size={16} style={{ position: 'absolute', left: '10px', top: '12px', color: '#9ca3af' }} />
                    <input style={{ ...inputStyle, paddingLeft: '34px', paddingRight: '38px' }} type={showPassword ? 'text' : 'password'} placeholder={t('login.passwordPlaceholder')} value={formData.password}
                      onChange={e => setFormData(p => ({ ...p, password: e.target.value }))} />
                    <button type="button" onClick={() => setShowPassword(v => !v)}
                      style={{ position: 'absolute', right: '8px', top: '8px', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '2px' }}>
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div style={{ textAlign: 'right', marginBottom: '16px' }}>
                  <button type="button" onClick={openForgot} style={linkButtonStyle}>
                    {t('login.forgotPassword')}
                  </button>
                </div>
                <button type="submit" className="btn btn-primary" disabled={formLoading}
                  style={{ width: '100%', padding: '12px', fontSize: '15px' }}>
                  {formLoading ? t('acceptInvite.loggingIn') : t('acceptInvite.loginAndAccept')}
                </button>
              </form>
              {googleOption}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AcceptInvite;
